import { CompRef } from '@wix/document-services-types';
import type { VirtualElementProvider } from './VirtualElementProvider';
import { ManifestQuery } from '../ManifestQuery';
import { ManifestResolver } from '../ManifestResolver';
import { VirtualManifestAPI } from '../VirtualManifestAPI';
import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../../errors';
import { IVirtualManifest, VirtualManifestElementType } from '../../builders';
import { BreakpointVariantNode } from '../../traverse/BreakpointVariantNode';
import { IOperationContext } from '../OperationContext';
import type {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

export enum VirtualElementAPIType {
  VirtualManifestAPI = 'VirtualManifestAPI',
  VirtualManifestAPIReadonly = 'VirtualManifestAPIReadonly',

  ManifestQuery = 'ManifestQuery',
  ManifestQueryReadonly = 'ManifestQueryReadonly',

  ManifestResolver = 'ManifestResolver',
}

export interface VirtualElementAPIMap {
  [VirtualElementAPIType.VirtualManifestAPI]: VirtualManifestAPI;
  [VirtualElementAPIType.VirtualManifestAPIReadonly]: VirtualManifestAPI;
  [VirtualElementAPIType.ManifestQuery]: ManifestQuery;
  [VirtualElementAPIType.ManifestQueryReadonly]: ManifestQuery;
  [VirtualElementAPIType.ManifestResolver]: ManifestResolver;
}

// allowed api transition types from the current node api type
const ALLOWED_API_TRANSITION = {
  [VirtualElementAPIType.ManifestResolver]: [
    VirtualElementAPIType.ManifestQueryReadonly,
    VirtualElementAPIType.VirtualManifestAPIReadonly,
    VirtualElementAPIType.ManifestResolver,
  ],
  [VirtualElementAPIType.ManifestQueryReadonly]: [
    VirtualElementAPIType.ManifestQueryReadonly,
    VirtualElementAPIType.VirtualManifestAPIReadonly,
    VirtualElementAPIType.ManifestResolver,
  ],
  [VirtualElementAPIType.VirtualManifestAPIReadonly]: [
    VirtualElementAPIType.ManifestQueryReadonly,
    VirtualElementAPIType.VirtualManifestAPIReadonly,
    VirtualElementAPIType.ManifestResolver,
  ],
  [VirtualElementAPIType.ManifestQuery]: null,
  [VirtualElementAPIType.VirtualManifestAPI]: null,
};

export class VirtualElement<TNodeAPIType extends VirtualElementAPIType> {
  static resolveVirtualManifest(virtualManifest: IVirtualManifest) {
    const childPath = virtualManifest.childPath;

    if (!childPath?.length) {
      return virtualManifest;
    }

    const tree = childPath.reduce(
      (acc, index) => acc?.children[index],
      virtualManifest.tree,
    );

    if (!tree) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ElementTraverseNode,
        'child path is invalid, child not found',
      );
    }

    return {
      ...virtualManifest,
      tree,
    };
  }

  #resolvedVirtualManifest: IVirtualManifest;
  #apiCache = new Map<
    VirtualElementAPIType,
    VirtualElementAPIMap[VirtualElementAPIType]
  >();

  constructor(
    private readonly virtualManifest: IVirtualManifest,
    private readonly editorPointerAPI: EditorPointerServiceAPI,
    private context?: {
      nodeAPIType?: TNodeAPIType;
      virtualElementProvider?: VirtualElementProvider;
      operationContext?: IOperationContext;
    },
  ) {
    this.#resolvedVirtualManifest = VirtualElement.resolveVirtualManifest(
      this.virtualManifest,
    );
  }

  lockOnResolverAPI() {
    if (this.context?.nodeAPIType) {
      const allowedTypes = ALLOWED_API_TRANSITION[this.context.nodeAPIType];
      if (
        allowedTypes &&
        !allowedTypes.includes(VirtualElementAPIType.ManifestResolver)
      ) {
        throw createEditorPlatformManifestServicesError(
          EditorPlatformManifestServicesErrorCode.ElementTraverseNode,
          'lockOnResolverAPI() node is already locked on specific API type',
        );
      }
    }

    return new VirtualElement(this.virtualManifest, this.editorPointerAPI, {
      ...this.context,
      nodeAPIType: VirtualElementAPIType.ManifestResolver,
    });
  }

  lockOnQueryAPI() {
    if (this.context?.nodeAPIType) {
      const allowedTypes = ALLOWED_API_TRANSITION[this.context.nodeAPIType];
      if (
        allowedTypes &&
        !allowedTypes.includes(VirtualElementAPIType.ManifestQuery)
      ) {
        throw createEditorPlatformManifestServicesError(
          EditorPlatformManifestServicesErrorCode.ElementTraverseNode,
          'lockOnQueryAPI() node is already locked on specific API type',
        );
      }
    }

    return new VirtualElement(this.virtualManifest, this.editorPointerAPI, {
      ...this.context,
      nodeAPIType: VirtualElementAPIType.ManifestQuery,
    });
  }

  lockOnReadonlyQueryAPI() {
    if (this.context?.nodeAPIType) {
      const allowedTypes = ALLOWED_API_TRANSITION[this.context.nodeAPIType];
      if (
        allowedTypes &&
        !allowedTypes.includes(VirtualElementAPIType.ManifestQueryReadonly)
      ) {
        throw createEditorPlatformManifestServicesError(
          EditorPlatformManifestServicesErrorCode.ElementTraverseNode,
          'lockOnReadonlyQueryAPI() node is already locked on specific API type',
        );
      }
    }

    return new VirtualElement(this.virtualManifest, this.editorPointerAPI, {
      ...this.context,
      nodeAPIType: VirtualElementAPIType.ManifestQueryReadonly,
    });
  }

  isMultipleComponentsRoot() {
    return this.isMultipleComponents() && this.isRoot();
  }

  isMultipleComponents() {
    return (
      this.virtualManifest.manifests.filter((x) => !x.isAutoInjected).length > 1
    );
  }

  isRoot() {
    if (this.#resolvedVirtualManifest.tree.editorPointer) {
      return false;
    }

    if (this.isMultipleComponents()) {
      return true;
    }

    const editorPointer = this.virtualManifest.manifests?.[0]?.editorPointer;

    return (
      !editorPointer.innerElementPath ||
      editorPointer.innerElementPath?.length === 0
    );
  }

  _getVirtualManifest() {
    return this.virtualManifest;
  }

  getExtensionType() {
    if (this.#resolvedVirtualManifest.tree.extensionType) {
      return this.#resolvedVirtualManifest.tree.extensionType;
    }

    if (this.isMultipleComponents()) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.MultipleComponentsSelected,
        'getExtensionType() is not available for root virtual manifest node in multi selection',
      );
    }

    return this.virtualManifest.manifests?.[0]?.store.extensionType;
  }

  getComponentType() {
    return this.#resolvedVirtualManifest.tree.componentType;
  }

  /**
   * TODO: respect multiselection
   */
  getManifestMetadata() {
    return this.virtualManifest.manifests[0].store.metadata;
  }

  getEditorPointers() {
    return this.virtualManifest.manifests.map((x) => x.editorPointer);
  }

  getEditorPointer() {
    if (this.#resolvedVirtualManifest.tree.editorPointer) {
      return this.#resolvedVirtualManifest.tree.editorPointer;
    }

    if (this.isMultipleComponents()) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.MultipleComponentsSelected,
        'getEditorPointer() is not available for root virtual manifest node in multi selection',
      );
    }

    return this.#resolvedVirtualManifest.manifests?.[0]?.editorPointer;
  }

  getParent() {
    if (this.isRoot()) {
      return null;
    }

    const childPath = [...this.getChildPath()];

    if (childPath.length) {
      return new VirtualElement(
        {
          ...this.virtualManifest,
          childPath: childPath.slice(0, -1),
        },
        this.editorPointerAPI,
        this.context,
      );
    }

    const editorPointer = this.getEditorPointer();
    const innerElementPath =
      this.editorPointerAPI.getInnerElementPath(editorPointer);

    if (innerElementPath?.length) {
      if (!this.context?.virtualElementProvider) {
        throw createEditorPlatformManifestServicesError(
          EditorPlatformManifestServicesErrorCode.ElementTraverseNode,
          'getParent() was not configured for current node context',
        );
      }

      const parentEditorPointer = this.editorPointerAPI.buildEditorPointer(
        this.editorPointerAPI.getDocumentPointer(editorPointer),
        innerElementPath.slice(0, -1),
      );

      return this.context.virtualElementProvider.buildVirtualElement({
        editorPointers: [parentEditorPointer],
      });
    }

    return null;
  }

  getParents() {
    const parents: VirtualElement<TNodeAPIType>[] = [];

    const collect = (node: VirtualElement<TNodeAPIType>) => {
      // not sure if we need to try/catch here, but let's keep it for now
      try {
        const _parent = node.getParent();
        if (_parent) {
          parents.push(_parent);
          collect(_parent);
        }
      } catch (e) {}
    };

    collect(this);

    return parents;
  }

  getChildren(
    options: {
      recursive?: boolean;
    } = {
      recursive: false,
    },
  ) {
    const children: VirtualElement<TNodeAPIType>[] = [];

    const collect = (node: VirtualElement<TNodeAPIType>) => {
      const _children = node
        .getVirtualManifest()
        .tree.children.map((child, index) => {
          return new VirtualElement(
            {
              ...this.virtualManifest,
              childPath: [...node.getChildPath(), index],
            },
            this.editorPointerAPI,
            this.context,
          );
        });

      children.push(..._children);
      if (options?.recursive) {
        _children.forEach((child) => collect(child));
      }
    };

    collect(this);

    return children;
  }

  getChild(path: string[]) {
    const virtualManifest = this.virtualManifest;

    let tree = virtualManifest.tree;

    const currentElementsPath = this.getChildPath().map((index) => {
      tree = tree?.children?.[index];
      return tree?.elementName;
    });

    if (!tree) {
      console.warn(
        createEditorPlatformManifestServicesError(
          EditorPlatformManifestServicesErrorCode.ElementTraverseNode,
          'child not found',
        ).withMessage(path.join('.')),
      );

      return null;
    }

    const focusElementsPath = [...currentElementsPath, ...path].join('.');

    const focusChildPath = virtualManifest.innerElementsIndexes
      .map((x) => x.elements[focusElementsPath])
      .filter((x) => !!x)?.[0];

    if (!focusChildPath) {
      console.warn(
        createEditorPlatformManifestServicesError(
          EditorPlatformManifestServicesErrorCode.ElementTraverseNode,
          'child map not found',
        )
          .withMessage(path.join('.'))
          .withMessage(focusElementsPath),
      );

      return null;
    }

    return new VirtualElement(
      {
        ...this.virtualManifest,
        childPath: focusChildPath,
      },
      this.editorPointerAPI,
      this.context,
    );
  }

  getRoot() {
    if (this.isRoot()) {
      return this;
    }

    const parents = this.getParents();
    return parents[parents.length - 1];
  }

  getElementType() {
    return this.#resolvedVirtualManifest.tree.elementType;
  }

  isRefElement() {
    return this.getElementType() === VirtualManifestElementType.RefElement;
  }

  getOperationalContext() {
    return this.context?.operationContext;
  }

  /**
   * NOTE: this manifest does not include inner elements
   */
  getManifest() {
    return this.#resolvedVirtualManifest.tree.manifest;
  }

  getChildPath() {
    return this.virtualManifest.childPath ?? [];
  }

  getVirtualManifest() {
    return this.#resolvedVirtualManifest;
  }

  withOperationContext(operationContext: IOperationContext) {
    return new VirtualElement(this.virtualManifest, this.editorPointerAPI, {
      ...this.context,
      operationContext,
    });
  }

  withChildPath(additionalChildPath: number[]) {
    if (!additionalChildPath.length) {
      return this;
    }

    const newChildPath = this.virtualManifest.childPath
      ? [...this.virtualManifest.childPath, ...additionalChildPath]
      : additionalChildPath;

    return new VirtualElement(
      { ...this.virtualManifest, childPath: newChildPath },
      this.editorPointerAPI,
      this.context,
    );
  }

  extract(editorPointer: EditorPointer<CompRef>) {
    if (!this.context?.virtualElementProvider) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ElementTraverseNode,
        'manifest api provider was not configured for current node context',
      );
    }

    if (
      !this.virtualManifest.manifests.some(
        (x) =>
          this.editorPointerAPI.buildEditorPointer(
            this.editorPointerAPI.getDocumentPointer(x.editorPointer),
          ).asUniqueKey ===
          this.editorPointerAPI.buildEditorPointer(
            this.editorPointerAPI.getDocumentPointer(editorPointer),
          ).asUniqueKey,
      )
    ) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ElementTraverseNode,
        'editor pointer does not belong to current virtual manifest',
      );
    }

    if (
      !this.isMultipleComponents() &&
      this.getEditorPointer().asUniqueKey === editorPointer.asUniqueKey
    ) {
      return this;
    }

    return this.context.virtualElementProvider.buildVirtualElement({
      editorPointers: [editorPointer],
    });
  }

  getAPI<TRequestedNodeAPIType extends VirtualElementAPIType = TNodeAPIType>(
    requestedAPI?: TRequestedNodeAPIType,
  ): VirtualElementAPIMap[TRequestedNodeAPIType] {
    if (!this.context?.virtualElementProvider) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ElementTraverseNode,
        'manifest api provider was not configured for current node context',
      );
    }

    if (requestedAPI && this.context.nodeAPIType) {
      const allowedTypes = ALLOWED_API_TRANSITION[this.context.nodeAPIType];

      if (allowedTypes && !allowedTypes.includes(requestedAPI)) {
        throw createEditorPlatformManifestServicesError(
          EditorPlatformManifestServicesErrorCode.ElementTraverseNode,
          'getManifestAPI() api type transition is not allowed',
        );
      }
    }

    const nodeAPIType = requestedAPI ?? this.context?.nodeAPIType;

    const cachedAPIInstance = this.#apiCache.get(nodeAPIType!);
    if (cachedAPIInstance) {
      return cachedAPIInstance as VirtualElementAPIMap[TRequestedNodeAPIType];
    }

    const isReadonly = [
      VirtualElementAPIType.ManifestQueryReadonly,
      VirtualElementAPIType.VirtualManifestAPIReadonly,
    ].includes(nodeAPIType!);

    const api = this.context.virtualElementProvider.createVirtualManifestAPI(
      new VirtualElement(this.virtualManifest, this.editorPointerAPI, {
        ...this.context,
        nodeAPIType: VirtualElementAPIType.VirtualManifestAPI,
      }),
      isReadonly,
    );

    let result: VirtualElementAPIMap[TRequestedNodeAPIType];

    switch (nodeAPIType) {
      case VirtualElementAPIType.ManifestQuery:
        result = api.query as VirtualElementAPIMap[TRequestedNodeAPIType];
        break;

      case VirtualElementAPIType.ManifestQueryReadonly:
        result = api.query as VirtualElementAPIMap[TRequestedNodeAPIType];
        break;

      case VirtualElementAPIType.ManifestResolver:
        result = api.resolver as VirtualElementAPIMap[TRequestedNodeAPIType];
        break;

      case VirtualElementAPIType.VirtualManifestAPI:
        result = api as VirtualElementAPIMap[TRequestedNodeAPIType];
        break;

      case VirtualElementAPIType.VirtualManifestAPIReadonly:
        result = api as VirtualElementAPIMap[TRequestedNodeAPIType];
        break;

      default:
        throw createEditorPlatformManifestServicesError(
          EditorPlatformManifestServicesErrorCode.ElementTraverseNode,
          'getManifestAPI() unresolved node API type',
        );
    }

    this.#apiCache.set(nodeAPIType!, result);
    return result;
  }

  withBreakpointVariant(editorPointer: EditorPointer<CompRef>) {
    const breakpointVariantNode = new BreakpointVariantNode(
      this.editorPointerAPI,
      this.virtualManifest,
      this,
    );

    return new VirtualElement(
      breakpointVariantNode.withBreakpointVariant(editorPointer),
      this.editorPointerAPI,
      this.context,
    );
  }
}
